import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader } from '../common';
import { FiX, FiPlus, FiCheck, FiChevronRight } from 'react-icons/fi';
import { IoWallet } from 'react-icons/io5';
import {
  subscriptionCheckoutPreview,
  createSubscriptionOrder,
  verifySubscription,
  fetchRazorpayKeyId,
  createWalletDepositOrder,
  verifyWalletDeposit,
  type SubscriptionTenure,
  type CheckoutPreview,
} from '../../api/expert';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: 'pro' | 'premium';
  tenure: SubscriptionTenure;
  onSuccess: (data: {
    subscription_tier: string;
    subscription_tenure: string;
    subscription_expires: string;
    wallet_balance: string;
  }) => void;
}

const TOPUP_PRESETS = [100, 500, 1000, 2000];

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  plan,
  tenure,
  onSuccess,
}) => {
  const colors = useAppSelector((state) => state.theme.theme);

  const [preview, setPreview] = useState<CheckoutPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Inline top-up state
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupProcessing, setTopupProcessing] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await subscriptionCheckoutPreview(plan, tenure);
      setPreview(data);
    } catch {
      setError('Could not load checkout details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [plan, tenure]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
      setShowTopup(false);
      setTopupAmount('');
    }
  }, [isOpen, loadPreview]);

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) return;

    setTopupProcessing(true);
    try {
      const keyId = await fetchRazorpayKeyId();
      const order = await createWalletDepositOrder(amount);

      const options = {
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Yantrai',
        description: `Wallet Top-up - Rs ${amount}`,
        order_id: order.order_id,
        handler: async (response: any) => {
          try {
            await verifyWalletDeposit({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setTopupAmount('');
            setShowTopup(false);
            // Re-fetch preview with updated wallet balance
            await loadPreview();
          } catch {
            setError('Top-up verification failed.');
          } finally {
            setTopupProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setTopupProcessing(false);
          },
        },
        theme: { color: colors.primary },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setError('Top-up payment failed.');
        setTopupProcessing(false);
      });
      rzp.open();
    } catch {
      setError('Could not initiate top-up.');
      setTopupProcessing(false);
    }
  };

  const handlePay = async () => {
    if (!preview) return;
    setProcessing(true);
    setError('');

    try {
      const useWallet = preview.payment_method !== 'razorpay_only';
      const result = await createSubscriptionOrder(plan, tenure, useWallet);

      // --- Wallet-only: instant activation ---
      if (result.payment_method === 'wallet_only') {
        onSuccess({
          subscription_tier: result.subscription_tier,
          subscription_tenure: result.subscription_tenure,
          subscription_expires: result.subscription_expires,
          wallet_balance: result.wallet_balance,
        });
        return;
      }

      // --- Razorpay flow ---
      const keyId = await fetchRazorpayKeyId();
      const options = {
        key: keyId,
        amount: result.amount,
        currency: result.currency,
        name: 'Yantrai',
        description: `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan - ${tenure.replace('_', '-')}`,
        order_id: result.order_id,
        handler: async (response: any) => {
          try {
            const verifyResult = await verifySubscription({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan,
              tenure,
            });
            onSuccess({
              subscription_tier: verifyResult.subscription_tier,
              subscription_tenure: verifyResult.subscription_tenure,
              subscription_expires: verifyResult.subscription_expires,
              wallet_balance: verifyResult.wallet_balance,
            });
          } catch {
            setError('Payment verification failed. Contact support if money was deducted.');
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
        theme: { color: colors.primary },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setError('Payment failed. Please try again.');
        setProcessing(false);
      });
      rzp.open();
    } catch {
      setError('Could not initiate payment. Please try again.');
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  const formatRs = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return `\u20B9${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !processing) onClose(); }}
    >
      <div
        className="w-full md:max-w-md rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${colors.borderColor}`,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b sticky top-0 z-10"
          style={{
            borderColor: colors.borderColor,
            backgroundColor: colors.backgroundColor,
          }}
        >
          <CText type="B18" color={colors.textColor}>
            Checkout
          </CText>
          <button
            onClick={onClose}
            disabled={processing}
            className="p-2 rounded-lg hover:opacity-80"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(FiX as any, { size: 18, color: colors.textColor })}
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <CLoader />
            </div>
          ) : preview ? (
            <>
              {/* Plan Summary */}
              <div
                className="p-4 rounded-xl"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}15, ${colors.primary}05)`,
                  border: `1px solid ${colors.primary}30`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
                      {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
                    </CText>
                    <CText type="r12" color={colors.grayScale500}>
                      {tenure === 'monthly' ? 'Monthly' : tenure === 'half_yearly' ? 'Half-Yearly' : 'Yearly'}
                      {` \u2022 ${preview.days} days`}
                    </CText>
                  </div>
                  <CText type="B18" color={colors.textColor}>
                    {formatRs(preview.plan_price)}
                  </CText>
                </div>
              </div>

              {/* Price Breakdown */}
              <div
                className="p-4 rounded-xl space-y-3"
                style={{
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <div className="flex justify-between">
                  <CText type="r14" color={colors.grayScale500}>Plan Price</CText>
                  <CText type="r14" color={colors.textColor}>{formatRs(preview.plan_price)}</CText>
                </div>

                {parseFloat(preview.proration_credit) > 0 && (
                  <div className="flex justify-between">
                    <CText type="r14" color={colors.green || '#1DCE5C'}>Proration Credit</CText>
                    <CText type="r14" color={colors.green || '#1DCE5C'}>
                      -{formatRs(preview.proration_credit)}
                    </CText>
                  </div>
                )}

                <div
                  className="border-t pt-3 flex justify-between"
                  style={{ borderColor: colors.borderColor }}
                >
                  <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                    Amount Due
                  </CText>
                  <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                    {formatRs(preview.effective_price)}
                  </CText>
                </div>

                {parseFloat(preview.wallet_deduction) > 0 && (
                  <div className="flex justify-between">
                    <div className="flex items-center gap-1">
                      {React.createElement(IoWallet as any, { size: 14, color: colors.primary })}
                      <CText type="r14" color={colors.primary}>Wallet</CText>
                    </div>
                    <CText type="r14" color={colors.primary}>
                      -{formatRs(preview.wallet_deduction)}
                    </CText>
                  </div>
                )}

                {parseFloat(preview.razorpay_amount) > 0 && (
                  <div className="flex justify-between">
                    <CText type="r14" color={colors.grayScale500}>Razorpay</CText>
                    <CText type="r14" color={colors.textColor}>
                      {formatRs(preview.razorpay_amount)}
                    </CText>
                  </div>
                )}
              </div>

              {/* Wallet Balance + Top-up */}
              <div
                className="p-4 rounded-xl"
                style={{
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {React.createElement(IoWallet as any, { size: 16, color: colors.primary })}
                    <CText type="r14" color={colors.grayScale500}>Wallet Balance</CText>
                  </div>
                  <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                    {formatRs(preview.wallet_balance)}
                  </CText>
                </div>

                {/* Add Money toggle */}
                <button
                  onClick={() => setShowTopup(!showTopup)}
                  className="flex items-center gap-1 mt-2 hover:opacity-80"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {React.createElement(FiPlus as any, { size: 14, color: colors.primary })}
                  <CText type="r12" color={colors.primary} style={{ fontWeight: 500 }}>
                    Add Money to Wallet
                  </CText>
                  {React.createElement(FiChevronRight as any, {
                    size: 14,
                    color: colors.primary,
                    style: { transform: showTopup ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' },
                  })}
                </button>

                {showTopup && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {TOPUP_PRESETS.map((preset) => (
                        <button
                          key={preset}
                          onClick={() => setTopupAmount(String(preset))}
                          className="px-3 py-1.5 rounded-lg border text-sm transition-colors"
                          style={{
                            backgroundColor: topupAmount === String(preset)
                              ? colors.primary
                              : colors.backgroundColor,
                            borderColor: topupAmount === String(preset)
                              ? colors.primary
                              : colors.borderColor,
                            color: topupAmount === String(preset)
                              ? '#fff'
                              : colors.textColor,
                            cursor: 'pointer',
                          }}
                        >
                          {`\u20B9${preset}`}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={topupAmount}
                        onChange={(e) => setTopupAmount(e.target.value)}
                        placeholder="Amount"
                        min="1"
                        className="flex-1 px-3 py-2 rounded-lg border outline-none text-sm"
                        style={{
                          backgroundColor: colors.backgroundColor,
                          borderColor: colors.borderColor,
                          color: colors.textColor,
                        }}
                      />
                      <button
                        onClick={handleTopup}
                        disabled={topupProcessing || !topupAmount}
                        className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                        style={{
                          backgroundColor: colors.primary,
                          color: '#fff',
                          cursor: topupProcessing || !topupAmount ? 'not-allowed' : 'pointer',
                          border: 'none',
                        }}
                      >
                        {topupProcessing ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: (colors.alertColor || '#E82C81') + '15' }}
                >
                  <CText type="r12" color={colors.alertColor || '#E82C81'}>
                    {error}
                  </CText>
                </div>
              )}

              {/* Pay Button */}
              <button
                onClick={handlePay}
                disabled={processing}
                className="w-full py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
                  color: '#fff',
                  border: 'none',
                  cursor: processing ? 'not-allowed' : 'pointer',
                  boxShadow: colors.shadowLarge,
                }}
              >
                {processing ? (
                  'Processing...'
                ) : preview.payment_method === 'wallet_only' ? (
                  <span className="flex items-center justify-center gap-2">
                    {React.createElement(FiCheck as any, { size: 18 })}
                    Activate Now
                  </span>
                ) : (
                  `Pay ${formatRs(preview.razorpay_amount)} via Razorpay`
                )}
              </button>

              <CText
                type="r12"
                color={colors.grayScale500}
                style={{ textAlign: 'center', display: 'block' }}
              >
                Auto-renews from wallet. Cancel anytime.
              </CText>
            </>
          ) : (
            <div className="text-center py-8">
              <CText type="r14" color={colors.alertColor || '#E82C81'}>
                {error || 'Could not load checkout details.'}
              </CText>
              <button
                onClick={loadPreview}
                className="mt-4 px-4 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: colors.primary,
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
